import { useRef } from "react";
import { getFileName } from "./common";
import { useUnmount } from "react-use";

export function useFileSystem() {
    const controllerRef = useRef<AbortController>(new AbortController());
    const rootHandleRef = useRef<FileSystemDirectoryHandle>();

    useUnmount(() => {
        controllerRef.current.abort();
    });

    async function init() {
        return (rootHandleRef.current = await showDirectoryPicker());
    }

    function checkAlive() {
        const error = { code: "cancel" };
        if (!controllerRef.current) throw error;
        if (controllerRef.current.signal.aborted) throw error;
    }

    function createDir(name: string, dir?: FileSystemDirectoryHandle) {
        checkAlive();
        const handle = dir || rootHandleRef.current;
        if (!handle) throw new Error("需要指定目录");
        return handle.getDirectoryHandle(name, { create: true });
    }

    function createFile(name: string, dir: FileSystemDirectoryHandle) {
        checkAlive();
        return dir.getFileHandle(name, { create: true });
    }

    async function isFileExist(name: string, dir: FileSystemDirectoryHandle) {
        const files = dir.entries();

        for await (const [filename] of files) {
            if (filename === name) return true;
        }

        return false;
    }

    async function saveBlob(
        filename: string,
        data: FileSystemWriteChunkType,
        dir: FileSystemDirectoryHandle
    ) {
        checkAlive();
        const file = await dir.getFileHandle(filename, { create: true });
        const writeable = await file.createWritable();
        await writeable.write(data);
        await writeable.close();
    }

    async function downloadFile(url: string, dir: FileSystemDirectoryHandle) {
        checkAlive();
        if (!url.includes("niu.edufe.com.cn")) return;

        controllerRef.current = new AbortController();

        const res = await fetch(url, { signal: controllerRef.current.signal });
        if (res.status !== 200 || !res.body) throw res.statusText;

        // const DownloadTimeout = 10 * 60 * 1000;

        // setTimeout(() => {
        //     controllerRef.current.abort();
        // }, DownloadTimeout);

        const filename = getFileName(url);
        const file = await createFile(filename, dir);

        await res.body.pipeTo(await file.createWritable());
    }

    async function downloadFiles(
        urls: string[],
        dir: FileSystemDirectoryHandle
    ) {
        checkAlive();
        for (const url of urls) {
            await downloadFile(url, dir);
        }
    }

    return {
        init,
        controllerRef,
        rootHandleRef,
        isFileExist,
        createFile,
        createDir,
        saveBlob,
        downloadFile,
        downloadFiles,
        checkAlive
    };
}
